/**
 * @fileoverview Inbox page (MOR-716).
 *
 * URL: /dashboard/inbox — tabs for Inbox / Snoozed / Done over the user's
 * notifications, with two layouts: grouped by task (default) or a pure
 * chronological feed with the task as a leading column. Clicking a row opens
 * the task side panel (?task=<id>) via a direct single-task lookup — tasks on
 * jobs the user can't access explain themselves instead of dead-clicking.
 * The gear opens the global notification settings (?settings=1).
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, usePaginatedQuery, useQuery } from "convex/react";
import {
  Check,
  CheckCheck,
  Clock,
  Inbox as InboxIcon,
  Layers,
  Mail,
  MailOpen,
  Rows3,
  Settings2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { KeyDateSidePanel } from "@/calendar";
import { Button } from "@/ui/primitives/button";
import { Spinner } from "@/ui/primitives/spinner";
import { Tabs, TabsList, TabsTrigger } from "@/ui/composed/sections/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import { NotificationRow, type InboxRow } from "./notification-row";
import { NotificationSettingsDialog } from "./notification-settings-dialog";
import { SnoozeMenu } from "./snooze-menu";

type InboxTab = "inbox" | "snoozed" | "done";
type InboxView = "grouped" | "feed";

const VIEW_STORAGE_KEY = "morgan-inbox-view";

function loadView(): InboxView {
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === "feed"
      ? "feed"
      : "grouped";
  } catch {
    return "grouped";
  }
}

/** Group rows by task, preserving newest-first order of first appearance.
 * Automation rows without a task (MOR-736) each form their own group. */
function groupByTask(rows: InboxRow[]): { taskId: string; rows: InboxRow[] }[] {
  const groups = new Map<string, InboxRow[]>();
  for (const row of rows) {
    const key = row.taskId ?? row._id;
    const existing = groups.get(key);
    if (existing) existing.push(row);
    else groups.set(key, [row]);
  }
  return Array.from(groups.entries()).map(([taskId, groupRows]) => ({
    taskId,
    rows: groupRows,
  }));
}

export function InboxPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/_app/_auth/dashboard/_layout/inbox" });

  const [tab, setTabState] = useState<InboxTab>("inbox");
  const [view, setView] = useState<InboxView>(loadView);
  // Bulk selection (MOR-716) — cleared when switching tabs.
  const [selectedIds, setSelectedIds] = useState<Set<Id<"notifications">>>(
    new Set(),
  );

  const setTab = (t: InboxTab) => {
    setTabState(t);
    setSelectedIds(new Set());
  };

  const toggleSelect = (id: Id<"notifications">) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setViewPersisted = (v: InboxView) => {
    setView(v);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, v);
    } catch {
      // Private-mode storage failures just lose the preference.
    }
  };

  const { results, status, loadMore } = usePaginatedQuery(
    api.notifications.inbox.list,
    { tab },
    { initialNumItems: 30 },
  );
  const markAllRead = useMutation(api.notifications.inbox.markAllRead);
  const markTaskRead = useMutation(api.notifications.inbox.markTaskRead);
  const bulkAction = useMutation(api.notifications.inbox.bulkAction);

  const runBulk = async (
    action: "read" | "unread" | "done" | "snooze" | "unsnooze",
    snoozeUntil?: number,
  ) => {
    const ids = [...selectedIds];
    // Backend caps at 100 per call; chunk to stay under it.
    for (let i = 0; i < ids.length; i += 100) {
      await bulkAction({
        notificationIds: ids.slice(i, i + 100),
        action,
        snoozeUntil,
      });
    }
    setSelectedIds(new Set());
  };

  // Direct single-task lookup for the ?task= panel target.
  const panelTarget = useQuery(
    api.keyDates.queries.getPanelTarget,
    search.task ? { taskId: search.task as Id<"jobKeyDates"> } : "skip",
  );

  const clearTask = () => {
    void navigate({
      to: "/dashboard/inbox",
      search: { task: undefined, settings: undefined },
    });
  };

  // Tasks the viewer can't open explain themselves instead of dead-clicking.
  useEffect(() => {
    if (!search.task || !panelTarget) return;
    if (panelTarget.status === "no_access") {
      toast.error(
        `You don't have access to ${panelTarget.jobName}. Ask the job owner to add you to open "${panelTarget.taskTitle}".`,
      );
    } else if (panelTarget.status === "not_found") {
      toast.error("This task no longer exists.");
    } else {
      return;
    }
    void navigate({
      to: "/dashboard/inbox",
      search: { task: undefined, settings: undefined },
    });
  }, [panelTarget, search.task, navigate]);

  const rows = useMemo(() => results ?? [], [results]);
  const groups = useMemo(() => groupByTask(rows), [rows]);

  const openTask = (taskId: Id<"jobKeyDates">) => {
    void markTaskRead({ taskId });
    void navigate({
      to: "/dashboard/inbox",
      search: { task: taskId as string, settings: undefined },
    });
  };

  const handleMarkAllRead = async () => {
    // Bounded server batches; loop until drained.
    for (let i = 0; i < 20; i++) {
      const { remaining } = await markAllRead({});
      if (!remaining) break;
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-5 bg-muted p-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Inbox</h1>
          <p className="text-sm text-muted-foreground">
            Activity on tasks you're assigned to, assigned out, or watching.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {tab === "inbox" && (
            <Button variant="outline" size="sm" onClick={handleMarkAllRead}>
              <CheckCheck className="size-4" />
              Mark all read
            </Button>
          )}
          <Button
            variant="outline"
            size="icon"
            aria-label="Notification settings"
            onClick={() =>
              void navigate({
                to: "/dashboard/inbox",
                search: { task: undefined, settings: 1 },
              })
            }
          >
            <Settings2 className="size-4" />
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Tabs value={tab} onValueChange={(v) => setTab(v as InboxTab)}>
          <TabsList>
            <TabsTrigger value="inbox">Inbox</TabsTrigger>
            <TabsTrigger value="snoozed">Snoozed</TabsTrigger>
            <TabsTrigger value="done">Done</TabsTrigger>
          </TabsList>
        </Tabs>
        <TooltipProvider>
          <div className="flex items-center gap-0.5 rounded-md border bg-white p-0.5 dark:bg-card">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={view === "grouped" ? "secondary" : "ghost"}
                  size="icon"
                  className="size-7"
                  aria-label="Group by task"
                  onClick={() => setViewPersisted("grouped")}
                >
                  <Layers className="size-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                Grouped by task
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={view === "feed" ? "secondary" : "ghost"}
                  size="icon"
                  className="size-7"
                  aria-label="Chronological feed"
                  onClick={() => setViewPersisted("feed")}
                >
                  <Rows3 className="size-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                Chronological feed
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>

      <div className="flex flex-1 flex-col gap-3">
        {status === "LoadingFirstPage" ? (
          <div className="flex h-48 items-center justify-center">
            <Spinner className="text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex h-48 flex-col items-center justify-center gap-2 text-muted-foreground">
            <InboxIcon className="size-8" />
            <p className="text-sm">
              {tab === "inbox"
                ? "You're all caught up."
                : tab === "snoozed"
                  ? "Nothing snoozed."
                  : "Nothing marked done yet."}
            </p>
          </div>
        ) : view === "feed" ? (
          <div className="rounded-lg border bg-white p-1 shadow-sm dark:bg-card">
            {rows.map((row) => (
              <NotificationRow
                key={row._id}
                row={row}
                tab={tab}
                showTask
                selected={selectedIds.has(row._id)}
                selectionActive={selectedIds.size > 0}
                onToggleSelect={toggleSelect}
                onOpen={openTask}
              />
            ))}
          </div>
        ) : (
          groups.map((group) => (
            <div
              key={group.taskId}
              className="rounded-lg border bg-white shadow-sm dark:bg-card"
            >
              <button
                type="button"
                className="flex w-full items-baseline gap-2 border-b px-3 py-2 text-left"
                onClick={() =>
                  group.rows[0].taskId &&
                  openTask(group.taskId as Id<"jobKeyDates">)
                }
              >
                <span className="truncate text-sm font-semibold">
                  {group.rows[0].taskTitle}
                </span>
                <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                  {group.rows[0].jobName}
                </span>
                {group.rows.length > 1 && (
                  <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                    {group.rows.length} updates
                  </span>
                )}
              </button>
              <div className="flex flex-col p-1">
                {group.rows.map((row) => (
                  <NotificationRow
                    key={row._id}
                    row={row}
                    tab={tab}
                    selected={selectedIds.has(row._id)}
                    selectionActive={selectedIds.size > 0}
                    onToggleSelect={toggleSelect}
                    onOpen={openTask}
                  />
                ))}
              </div>
            </div>
          ))
        )}

        {status === "CanLoadMore" && (
          <Button
            variant="outline"
            size="sm"
            className="self-center"
            onClick={() => loadMore(30)}
          >
            Load more
          </Button>
        )}
      </div>

      {/* Bulk action bar — mirrors the task list's floating selection pill. */}
      {selectedIds.size > 0 && (
        <div className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-4">
          <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary px-4 py-2 shadow-lg">
            <span className="text-sm font-medium text-primary-foreground">
              {selectedIds.size} selected
            </span>
            <div className="h-4 w-px bg-primary-foreground/20" />
            {tab === "inbox" && (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 gap-1.5 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                  onClick={() => void runBulk("read")}
                >
                  <MailOpen className="size-3.5" />
                  Read
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 gap-1.5 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                  onClick={() => void runBulk("unread")}
                >
                  <Mail className="size-3.5" />
                  Unread
                </Button>
                <SnoozeMenu
                  onSnooze={(until) => void runBulk("snooze", until)}
                  trigger={
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 gap-1.5 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                    >
                      <Clock className="size-3.5" />
                      Snooze
                    </Button>
                  }
                />
              </>
            )}
            {tab === "snoozed" && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 gap-1.5 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                onClick={() => void runBulk("unsnooze")}
              >
                <Mail className="size-3.5" />
                Unsnooze
              </Button>
            )}
            {tab !== "done" && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 gap-1.5 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                onClick={() => void runBulk("done")}
              >
                <Check className="size-3.5" />
                Done
              </Button>
            )}
            {tab === "done" && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 gap-1.5 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                onClick={() => void runBulk("unread")}
              >
                <Mail className="size-3.5" />
                Reopen
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-7 gap-1.5 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
              onClick={() => setSelectedIds(new Set())}
            >
              <X className="size-3.5" />
              Clear
            </Button>
          </div>
        </div>
      )}

      <KeyDateSidePanel
        state={
          panelTarget?.status === "ok"
            ? { kind: "view", event: panelTarget.event }
            : { kind: "closed" }
        }
        onClose={clearTask}
      />

      <NotificationSettingsDialog
        open={search.settings === 1}
        onOpenChange={(open) => {
          if (!open) clearTask();
        }}
      />
    </div>
  );
}
